import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import dayjs from 'dayjs';

import PercentOutlined from '@mui/icons-material/PercentOutlined';

import useTaxReport from 'hooks/reports/useTaxReport';
import ReportHeader from 'components/reports/ReportHeader';
import ReportFilters from 'components/reports/ReportFilters';
import ReportSummaryCards from 'components/reports/ReportSummaryCards';
import ReportTable from 'components/reports/ReportTable';
import { RevenueTrendChart, ReportBarChart } from 'components/reports/ReportCharts';
import ExportActions from 'components/reports/ExportActions';
import EmptyState from 'components/reports/EmptyState';
import LoadingState from 'components/reports/LoadingState';

import { useCurrency } from 'contexts/CurrencyContext';
import { formatCurrency } from 'utils/currency';
import CurrencyAmount from 'components/currency/CurrencyAmount';

const getErrorMessage = (error) => {
  if (!error) return '';
  const data = error.response?.data;
  if (data) {
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return `${data.message}: ${data.errors.join(', ')}`;
    }
    return data.message || data.error || error.message;
  }
  return error.message;
};

export default function TaxReport() {
  const { currency } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL search params
  const startDate = searchParams.get('startDate') || dayjs().subtract(30, 'day').format('YYYY-MM-DD');
  const endDate = searchParams.get('endDate') || dayjs().format('YYYY-MM-DD');
  const storeId = searchParams.get('storeId') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sortBy') || 'timestamp';
  const sortOrder = searchParams.get('sortOrder') || 'DESC';

  // Note: status filter is not used in TaxReport page filters
  const filters = { startDate, endDate, storeId, search, page, sortBy, sortOrder };

  // Fetch report data
  const { data, isLoading, error, refetch } = useTaxReport({
    startDate,
    endDate,
    storeId,
    search,
    page,
    limit: 10,
    sortBy,
    sortOrder
  });

  const handleFilterChange = (newFilters) => {
    const nextParams = {};
    Object.keys(newFilters).forEach((key) => {
      if (newFilters[key] !== undefined && newFilters[key] !== '') {
        nextParams[key] = String(newFilters[key]);
      }
    });
    setSearchParams(nextParams);
  };

  const handlePaginationModelChange = (model) => {
    handleFilterChange({
      ...filters,
      page: model.page + 1
    });
  };

  const handleSortModelChange = (sortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      handleFilterChange({
        ...filters,
        sortBy: field,
        sortOrder: sort.toUpperCase(),
        page: 1
      });
    } else {
      handleFilterChange({
        ...filters,
        sortBy: 'timestamp',
        sortOrder: 'DESC',
        page: 1
      });
    }
  };

  const responseData = data?.data || {};
  const summary = responseData.summary || {};
  const byDate = responseData.by_date || [];
  const byStore = responseData.by_store || [];
  const listing = responseData.listing || [];
  const pagination = responseData.pagination || {};

  // Map byDate for AreaChart (reusing RevenueTrendChart which expects key 'revenue')
  const dateTrendData = byDate.map((item) => {
    const dStr = item.date ? dayjs(item.date).format('MMM D') : '—';
    return {
      date: dStr,
      revenue: item.tax_collected || 0
    };
  });

  // Summary Cards Configuration
  const summaryCards = [
    {
      label: 'Total Tax Collected',
      value: <CurrencyAmount value={summary.overall_tax_collected || 0} variant="h3" color="inherit" fontWeight={800} />,
      tooltip: 'Total VAT / sales tax collected (excludes Cancelled/Rejected)',
      icon: PercentOutlined,
      color: '#1f5b40'
    }
  ];

  // Table Columns
  const tableColumns = [
    { field: 'order_no', headerName: 'Order No', flex: 1, minWidth: 140, sortable: false },
    {
      field: 'Store',
      headerName: 'Store',
      flex: 1.4,
      minWidth: 180,
      sortable: false,
      valueGetter: (value, row) => row.Store?.title_en || '—'
    },
    {
      field: 'tax_total',
      headerName: 'Tax Amount',
      flex: 1.1,
      minWidth: 130,
      renderCell: (params) => <CurrencyAmount value={params.value || 0} />,
      valueFormatter: (params) => formatCurrency(params.value || 0, currency.code, currency.symbol)
    },
    {
      field: 'grand_total',
      headerName: 'Grand Total',
      flex: 1.1,
      minWidth: 130,
      renderCell: (params) => <CurrencyAmount value={params.value || 0} fontWeight={700} />,
      valueFormatter: (params) => formatCurrency(params.value || 0, currency.code, currency.symbol)
    },
    {
      field: 'timestamp',
      headerName: 'Date',
      flex: 1.2,
      minWidth: 160,
      valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString() : '')
    }
  ];

  const paginationModel = {
    page: page - 1,
    pageSize: 10
  };

  return (
    <Box sx={{ pb: 3 }}>
      <ReportHeader
        title="Tax Report"
        subtitle="Analyze VAT collection, tax summary by restaurant branch, daily tax timeline, and tax receipts."
        actions={<ExportActions data={listing} columns={tableColumns} title="Tax Report" />}
      />

      {/* Hide status filter for Tax report as it is not supported on the backend */}
      <ReportFilters filters={filters} onFilterChange={handleFilterChange} showStatusFilter={false} />

      {isLoading ? (
        <LoadingState cardCount={1} />
      ) : error ? (
        <EmptyState type="error" message={getErrorMessage(error)} onRetry={refetch} />
      ) : (
        <Box sx={{ animation: 'fadeIn 0.3s ease-out' }}>
          <ReportSummaryCards cards={summaryCards} />

          {listing.length > 0 && (
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              {/* Daily Tax Collection Area Chart */}
              <Grid size={{ xs: 12, md: 6 }}>
                <RevenueTrendChart data={dateTrendData} title="Tax by Date" />
              </Grid>

              {/* Tax Collection by Store Bar Chart */}
              <Grid size={{ xs: 12, md: 6 }}>
                <ReportBarChart data={byStore} xKey="store_name" yKey="tax_collected" name="Tax" title="Tax by Store" isCurrency />
              </Grid>
            </Grid>
          )}

          {listing.length === 0 ? (
            <EmptyState
              type={search || storeId ? 'nofilters' : 'nodata'}
              message={search || storeId ? undefined : 'No report data available'}
            />
          ) : (
            <ReportTable
              columns={tableColumns}
              rows={listing}
              loading={isLoading}
              rowCount={pagination.total || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              onSortModelChange={handleSortModelChange}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
